import { NavLink, Outlet } from 'react-router';
import { PageHeader } from '@/design-system/components/page-header';
import { useMe } from '@/lib/session';
import { cn } from '@/lib/utils';
import type { Permission } from '@cw/shared';

type Section = { to: string; label: string; phase?: number; permission?: Permission };

/** Configurações (SPEC §44). Seções sem fase marcada já funcionam. */
export const SETTINGS_SECTIONS: { label: string; items: Section[] }[] = [
  { label: 'Você', items: [{ to: 'account', label: 'Conta' }] },
  {
    label: 'Estúdio',
    items: [
      { to: 'organization', label: 'Organização' },
      { to: 'members', label: 'Membros e acessos', permission: 'members:read' },
      { to: 'workflow', label: 'Workflow' },
      { to: 'services', label: 'Serviços e preços' },
      { to: 'templates', label: 'Templates de projeto', permission: 'projects:create' },
      { to: 'finance', label: 'Financeiro', permission: 'finance:write' },
      { to: 'files', label: 'Arquivos', permission: 'org:manage' },

      { to: 'notifications', label: 'Notificações' },
      { to: 'appearance', label: 'Aparência' },
    ],
  },
];

export function SettingsLayout() {
  const me = useMe();
  const visible = (s: Section) => !s.permission || me.permissions.includes(s.permission);
  return (
    <div className="mx-auto w-full max-w-[1100px] pb-12">
      <PageHeader title="Configurações" />
      <div className="flex flex-col gap-6 px-4 md:flex-row md:gap-10 md:px-8">
        <nav
          aria-label="Seções"
          className="-mx-4 overflow-x-auto px-4 md:mx-0 md:w-52 md:shrink-0 md:overflow-visible md:px-0"
        >
          <div className="flex gap-1 md:flex-col md:gap-5">
            {SETTINGS_SECTIONS.map((g) => (
              <div key={g.label} className="flex gap-1 md:flex-col md:gap-0.5">
                <p className="hidden px-2.5 pb-1 text-[12px] text-fg-subtle md:block">{g.label}</p>
                {g.items.filter(visible).map((s) => (
                  <NavLink
                    key={s.to}
                    to={s.to}
                    className={({ isActive }) =>
                      cn(
                        'flex h-9 shrink-0 items-center justify-between gap-3 rounded-control px-2.5 text-[13.5px] whitespace-nowrap transition-colors',
                        isActive ? 'bg-raised text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
                      )
                    }
                  >
                    {s.label}
                    {s.phase && <span className="text-[11.5px] text-fg-subtle">fase {s.phase}</span>}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </nav>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-line pb-8 mb-8 last:mb-0 last:border-0">
      <div className="mb-5">
        <h2 className="text-[16px] font-semibold tracking-[-0.01em]">{title}</h2>
        {description && <p className="mt-1 text-[13.5px] text-fg-muted">{description}</p>}
      </div>
      {children}
    </section>
  );
}
